/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState } from 'react';
import { addInstrument } from '../../api/api';

const AddInstrumentForm = ({ onInstrumentAdded, onError }) => {
  const [symbol, setSymbol] = useState('');
  // Optional: Add fields for exchange and base_timeframe if your backend requires/supports them
  // const [exchange, setExchange] = useState(''); 
  // const [baseTimeframe, setBaseTimeframe] = useState('M1');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setSuccessMessage(null);

    if (!symbol.trim()) {
      setFormError("Instrument symbol is required.");
      if (onError) onError("Instrument symbol is required.");
      return;
    }

    // Construct instrumentData based on what backend expects.
    // If only symbol is needed:
    const instrumentData = { symbol: symbol.toUpperCase().trim() };
    // If exchange and timeframe are also needed:
    // const instrumentData = { 
    //   symbol: symbol.toUpperCase().trim(),
    //   exchange: exchange.toUpperCase().trim(), // Ensure you have input for this
    //   base_timeframe: baseTimeframe // Ensure you have input for this
    // };


    setIsSubmitting(true);
    try {
      // The API response for 201 might be the created instrument object or just a success message.
      // Let's assume it returns the instrument object as per typical REST practices.
      const newInstrument = await addInstrument(instrumentData);
      setSuccessMessage(`Instrument ${newInstrument.symbol || instrumentData.symbol} submitted. It will appear in the list once processed.`);
      if (onInstrumentAdded) {
        onInstrumentAdded(newInstrument); // Pass the newly added instrument data
      }
      setSymbol(''); // Reset form
      // setExchange(''); 
      // setBaseTimeframe('M1');
    } catch (error) {
      console.error("Failed to add instrument:", error);
      const errorMessage = error.detail || error.message || (typeof error === 'object' ? JSON.stringify(error) : error.toString());
      setFormError(`Failed to add instrument: ${errorMessage}`);
      if (onError) onError(`Failed to add instrument: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {formError && <p className="text-sm text-red-600 bg-red-100 p-2 rounded">{formError}</p>}
      {successMessage && <p className="text-sm text-green-600 bg-green-100 p-2 rounded">{successMessage}</p>}
      
      <div>
        <label htmlFor="instrumentSymbol" className="block text-sm font-medium text-textSecondary">
          New Instrument Symbol (e.g., EURUSD, BTCUSD)
        </label>
        <input
          type="text"
          id="instrumentSymbol"
          value={symbol}
          onChange={(e) => setSymbol(e.target.value)}
          placeholder="Enter symbol"
          className="mt-1 block w-full p-2 border border-borderDefault rounded-md shadow-sm focus:ring-primary focus:border-primary bg-backgroundInput text-textPrimary"
        />
      </div>

      {/* Optional: Add inputs for exchange and base_timeframe if needed by backend 
      <div>
        <label htmlFor="instrumentExchange" className="block text-sm font-medium text-textSecondary">Exchange (e.g., FXCM, BINANCE)</label>
        <input type="text" id="instrumentExchange" value={exchange} onChange={(e) => setExchange(e.target.value)} placeholder="Enter exchange (optional)" className="mt-1 block w-full p-2 border border-borderDefault rounded-md ..."/>
      </div>
      <div>
        <label htmlFor="baseTimeframe" className="block text-sm font-medium text-textSecondary">Base Timeframe</label>
        <select id="baseTimeframe" value={baseTimeframe} onChange={(e) => setBaseTimeframe(e.target.value)} className="mt-1 block w-full p-2 border border-borderDefault rounded-md ...">
          {["M1", "M5", "M15", "H1", "D1"].map(tf => <option key={tf} value={tf}>{tf}</option>)}
        </select>
      </div>
      */}

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full py-2 px-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-dark disabled:opacity-60"
      >
        {isSubmitting ? 'Submitting...' : 'Add Instrument'}
      </button>
    </form>
  );
};

export default AddInstrumentForm;
